import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { pick } from 'lodash';
import { formatMonth } from 'PDF/helpers/formatMonthPDF';

import { useGetOnePageOperatingLabels } from 'components/OnePageTemplates/constants/onePageSectionLabels';
import { useDataContext } from 'components/OnePageTemplates/context';
import { getFormattedNumber } from 'components/OnePageTemplates/helpers/getFormattedNumber';
import { IInfoCard } from 'components/OnePageTemplates/OnePageFirstStyle/components/InfoCard/InfoCard';
import { checkAmountWithDollarSign } from 'components/OnePageTemplates/OnePageFirstStyle/utils/checkAmountWithDollarSign';

import { hasMeaningfulValues } from 'helpers/hasMeaningfulValues';

import { InfoCard } from '../components/InfoCard';
import { TopSection } from '../TopSection';

import styles from './InfoSection.module.scss';

export const InfoSection: React.FC = () => {
  const { data, isExtendedPreview } = useDataContext();
  const { t } = useTranslation();
  const onePageSectionLabels = useGetOnePageOperatingLabels();

  const sectionData = pick(data, [
    'companyName',
    'projectBriefDescription',
    'projectStatus',
    'projectPaybackPeriod',
    'forecastedProfitability',
    'fundingAmount',
    'projectedAnnualIncome',
  ]);

  const isProjectedAnnualIncome = checkAmountWithDollarSign(data.projectedAnnualIncome ?? '');

  if (!hasMeaningfulValues(sectionData)) {
    return null;
  }

  const cardsToRender: IInfoCard[] = [
    isProjectedAnnualIncome &&
      data.projectedAnnualIncome && {
        label: onePageSectionLabels.projectedAnnualIncome,
        value: getFormattedNumber({ data: data.projectedAnnualIncome, startSymbol: '$' }),
      },
    data.projectPaybackPeriod &&
      data.projectPaybackPeriod !== '0 months' && {
        label: onePageSectionLabels.projectedPaybackPeriod,
        value: formatMonth(data.projectPaybackPeriod),
      },
    data.forecastedProfitability &&
      data.forecastedProfitability !== '0%' && {
        label: onePageSectionLabels.projectedProfitability,
        value: data.forecastedProfitability,
      },
    data.fundingAmount &&
      data.fundingAmount !== '0' && {
        label: onePageSectionLabels.fundingNeeds,
        value: data.fundingAmount,
      },
  ].filter(Boolean) as IInfoCard[];

  return (
    <Box className={styles.infoSection} component="header">
      <Box className={styles.wrapper}>
        <TopSection />
        <Box className={styles.sectionWrapper}>
          <Typography component="h1" variant="body1" className={styles.title}>
            {data.companyName}
          </Typography>
          {data.projectBriefDescription && (
            <Typography variant="body1" className={styles.content}>
              {data.projectBriefDescription}
            </Typography>
          )}
          {data.projectStatus && data.projectStatus?.length > 0 && isExtendedPreview && (
            <Typography variant="body1" className={styles.developmentStageContainer}>
              <Box className={styles.developmentStage} component="span">
                {t('onePageMapping.devStage')}:{' '}
              </Box>
              {t(`onePageMapping.projectStatuses.${data.projectStatus[0]}`)}
            </Typography>
          )}
        </Box>
        <Box className={clsx(styles.cardContainer, { [styles.containerWithOneCard]: cardsToRender.length === 1 })}>
          {cardsToRender.map((card) => (
            <InfoCard key={card.label} label={card.label} value={card.value} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
